import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Filter, Grid, List } from 'lucide-react';
import { Header } from '@/components/Header';
import { JobBoardFilterSidebar } from '@/components/JobBoardFilterSidebar';
import { JobBoardCard } from '@/components/JobBoardCard';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { useJobBoards } from '@/hooks/useJobBoards';
import type { JobBoardFilters } from '@/hooks/useJobBoards';

const sortOptions = [
  { value: 'relevance', label: 'Relevance' },
  { value: 'name', label: 'Name A-Z' },
  { value: 'updated', label: 'Recently Added' },
];

export default function JobBoards() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState(searchParams.get('q') || '');
  const [sortBy, setSortBy] = useState(searchParams.get('sort') || 'relevance');
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  
  const [filters, setFilters] = useState<JobBoardFilters>({
    categories: searchParams.get('category')?.split(',').filter(Boolean) || [],
    countries: searchParams.get('country')?.split(',').filter(Boolean) || [],
    remoteOnly: searchParams.get('remote') === 'true' ? true : null,
    jobTypes: searchParams.get('jobType')?.split(',').filter(Boolean) || [],
    niches: searchParams.get('niche')?.split(',').filter(Boolean) || [],
    workModes: searchParams.get('workMode')?.split(',').filter(Boolean) || [],
    geographies: searchParams.get('geography')?.split(',').filter(Boolean) || [],
  });

  const { data: jobBoards = [], isLoading, error, refetch } = useJobBoards(searchQuery, filters, sortBy);

  // Sync URL params
  useEffect(() => {
    const params = new URLSearchParams();
    
    if (searchQuery) params.set('q', searchQuery);
    if (sortBy !== 'relevance') params.set('sort', sortBy);
    if (filters.categories.length) params.set('category', filters.categories.join(','));
    if (filters.countries.length) params.set('country', filters.countries.join(','));
    if (filters.remoteOnly === true) params.set('remote', 'true');
    if (filters.jobTypes.length) params.set('jobType', filters.jobTypes.join(','));
    if (filters.niches.length) params.set('niche', filters.niches.join(','));
    if (filters.workModes.length) params.set('workMode', filters.workModes.join(','));
    if (filters.geographies.length) params.set('geography', filters.geographies.join(','));

    setSearchParams(params, { replace: true });
  }, [searchQuery, sortBy, filters, setSearchParams]);

  const activeFilterCount = 
    filters.categories.length + 
    filters.countries.length + 
    filters.jobTypes.length +
    filters.niches.length +
    filters.workModes.length +
    filters.geographies.length +
    (filters.remoteOnly !== null ? 1 : 0);

  const clearAllFilters = () => {
    setFilters({
      categories: [],
      countries: [],
      remoteOnly: null,
      jobTypes: [],
      niches: [],
      workModes: [],
      geographies: [],
    });
    setSearchQuery('');
  };

  const removeFilter = (type: keyof JobBoardFilters, value: string | boolean) => {
    if (type === 'remoteOnly') {
      setFilters(prev => ({ ...prev, remoteOnly: null }));
    } else {
      setFilters(prev => ({
        ...prev,
        [type]: (prev[type] as string[]).filter(item => item !== value)
      }));
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header 
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        placeholder="Search job boards..."
      />

      <div className="flex">
        <JobBoardFilterSidebar
          filters={filters}
          onFiltersChange={setFilters}
          {...(filtersOpen ? { isOpen: filtersOpen, onClose: () => setFiltersOpen(false) } : {})}
        />

        <main className="flex-1 min-w-0">
          <div className="container mx-auto px-4 py-8">
            {/* Page title */}
            <div className="mb-8">
              <h1 className="text-3xl font-heading font-bold text-text-primary mb-2">
                Design Job Boards
              </h1>
              <p className="text-text-secondary text-lg">
                Find your next design opportunity on curated job boards
              </p>
            </div>

            {/* Controls bar */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
              <div className="flex items-center space-x-4">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setFiltersOpen(true)}
                  className="lg:hidden"
                >
                  <Filter className="h-4 w-4 mr-2" />
                  Filters
                  {activeFilterCount > 0 && (
                    <Badge variant="secondary" className="ml-2 bg-accent-light text-accent">
                      {activeFilterCount}
                    </Badge>
                  )}
                </Button>

                <div className="text-sm text-text-secondary">
                  {isLoading ? (
                    "Loading job boards..."
                  ) : (
                    <>
                      <span className="font-medium text-text-primary">{jobBoards.length}</span> job boards found
                    </>
                  )}
                </div>
              </div>

              <div className="flex items-center space-x-4">
                {/* View mode toggle */}
                <div className="flex items-center border border-border rounded-lg p-1">
                  <Button
                    variant={viewMode === 'grid' ? 'default' : 'ghost'}
                    size="sm"
                    onClick={() => setViewMode('grid')}
                    className="h-8 px-3"
                  >
                    <Grid className="h-4 w-4" />
                  </Button>
                  <Button
                    variant={viewMode === 'list' ? 'default' : 'ghost'}
                    size="sm"
                    onClick={() => setViewMode('list')}
                    className="h-8 px-3"
                  >
                    <List className="h-4 w-4" />
                  </Button>
                </div>

                {/* Sort */}
                <Select value={sortBy} onValueChange={setSortBy}>
                  <SelectTrigger className="w-40">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {sortOptions.map(option => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Active filters */}
            {(activeFilterCount > 0 || searchQuery) && (
              <div className="flex flex-wrap items-center gap-2 mb-6 p-4 bg-surface rounded-lg">
                <span className="text-sm text-text-secondary">Active filters:</span>
                
                {searchQuery && (
                  <Badge variant="secondary" className="bg-accent-light text-accent">
                    Search: "{searchQuery}"
                    <button
                      onClick={() => setSearchQuery('')}
                      className="ml-1 hover:text-error transition-colors duration-fast"
                    >
                      ×
                    </button>
                  </Badge>
                )}

                {filters.jobTypes.map(jobType => (
                  <Badge key={jobType} variant="secondary">
                    {jobType.replace('_', ' ')}
                    <button
                      onClick={() => removeFilter('jobTypes', jobType)}
                      className="ml-1 hover:text-error transition-colors duration-fast"
                    >
                      ×
                    </button>
                  </Badge>
                ))}

                {filters.niches.map(niche => (
                  <Badge key={niche} variant="secondary">
                    {niche}
                    <button
                      onClick={() => removeFilter('niches', niche)}
                      className="ml-1 hover:text-error transition-colors duration-fast"
                    >
                      ×
                    </button>
                  </Badge>
                ))}

                {filters.workModes.map(workMode => (
                  <Badge key={workMode} variant="secondary">
                    {workMode}
                    <button
                      onClick={() => removeFilter('workModes', workMode)}
                      className="ml-1 hover:text-error transition-colors duration-fast"
                    >
                      ×
                    </button>
                  </Badge>
                ))}

                {filters.geographies.map(geography => (
                  <Badge key={geography} variant="secondary">
                    {geography}
                    <button
                      onClick={() => removeFilter('geographies', geography)}
                      className="ml-1 hover:text-error transition-colors duration-fast"
                    >
                      ×
                    </button>
                  </Badge>
                ))}

                {filters.categories.map(cat => {
                  const label = cat === 'design_niche' ? 'Design Niche' : 'General';
                  return (
                    <Badge key={cat} variant="secondary">
                      {label}
                      <button
                        onClick={() => removeFilter('categories', cat)}
                        className="ml-1 hover:text-error transition-colors duration-fast"
                      >
                        ×
                      </button>
                    </Badge>
                  );
                })}

                {filters.countries.map(country => (
                  <Badge key={country} variant="secondary">
                    {country}
                    <button
                      onClick={() => removeFilter('countries', country)}
                      className="ml-1 hover:text-error transition-colors duration-fast"
                    >
                      ×
                    </button>
                  </Badge>
                ))}

                {filters.remoteOnly && (
                  <Badge variant="secondary">
                    Remote-focused
                    <button
                      onClick={() => removeFilter('remoteOnly', true)}
                      className="ml-1 hover:text-error transition-colors duration-fast"
                    >
                      ×
                    </button>
                  </Badge>
                )}

                <Button
                  variant="ghost"
                  size="sm"
                  onClick={clearAllFilters}
                  className="text-text-tertiary hover:text-error text-sm"
                >
                  Clear all
                </Button>
              </div>
            )}

            {/* Results */}
            {error ? (
              <div className="text-center py-16">
                <div className="max-w-md mx-auto">
                  <div className="h-24 w-24 mx-auto mb-6 rounded-full bg-surface flex items-center justify-center">
                    <Filter className="h-8 w-8 text-text-tertiary" />
                  </div>
                  <h3 className="text-xl font-heading font-semibold text-text-primary mb-2">
                    Error loading job boards
                  </h3>
                  <p className="text-text-secondary mb-6">
                    {error.message}
                  </p>
                  <Button onClick={() => refetch()} variant="outline">
                    Try again
                  </Button>
                </div>
              </div>
            ) : isLoading ? (
              <div className="text-center py-16">
                <div className="text-text-secondary">Loading job boards...</div>
              </div>
            ) : jobBoards.length > 0 ? (
              <div className={
                viewMode === 'grid' 
                  ? "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 gap-6"
                  : "space-y-4"
              }>
                {jobBoards.map(jobBoard => (
                  <JobBoardCard key={jobBoard.id} jobBoard={jobBoard} />
                ))}
              </div>
            ) : (
              <div className="text-center py-16">
                <div className="max-w-md mx-auto">
                  <div className="h-24 w-24 mx-auto mb-6 rounded-full bg-surface flex items-center justify-center">
                    <Filter className="h-8 w-8 text-text-tertiary" />
                  </div>
                  <h3 className="text-xl font-heading font-semibold text-text-primary mb-2">
                    No job boards found
                  </h3>
                  <p className="text-text-secondary mb-6">
                    No matches for your current search and filters. Try clearing some filters or broadening your search.
                  </p>
                  <Button onClick={clearAllFilters} variant="outline">
                    Clear all filters
                  </Button>
                </div>
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
}